import { computed, type ComputedRef } from 'vue';

export type EntityType =
  | 'documento'
  | 'envio'
  | 'aviso'
  | 'filial'
  | 'cargo'
  | 'departamento'
  | 'registro'
  | 'registro gerado'
  | 'modelo'
  | 'rascunho'
  | 'usuário'
  | 'membro'
  | 'notificação'
  | 'atividade funcional'
  | 'local'
  | 'treinamento'
  | 'conteúdo'
  | 'colaborador'
  | 'log'
  | 'matriz';

export interface EntityConfig {
  plural: string;
  noneFoundMessage: string;
  gender?: 'male' | 'female';
}

export const ENTITY_CONFIGS: Record<EntityType, EntityConfig> = {
  documento: {
    plural: 'documentos',
    noneFoundMessage: 'Nenhum documento encontrado',
  },
  envio: {
    plural: 'envios',
    noneFoundMessage: 'Nenhum envio encontrado',
  },
  aviso: {
    plural: 'avisos',
    noneFoundMessage: 'Nenhum aviso encontrado',
  },
  filial: {
    plural: 'filiais',
    gender: 'female',
    noneFoundMessage: 'Nenhuma filial encontrada',
  },
  cargo: {
    plural: 'cargos',
    noneFoundMessage: 'Nenhum cargo encontrado',
  },
  departamento: {
    plural: 'departamentos',
    noneFoundMessage: 'Nenhum departamento encontrado',
  },
  registro: {
    plural: 'registros',
    noneFoundMessage: 'Nenhum registro encontrado',
  },
  'registro gerado': {
    plural: 'registros gerados',
    noneFoundMessage: 'Nenhum registro gerado encontrado',
  },
  modelo: {
    plural: 'modelos',
    noneFoundMessage: 'Nenhum modelo encontrado',
  },
  rascunho: {
    plural: 'rascunhos',
    noneFoundMessage: 'Nenhum rascunho encontrado',
  },
  usuário: {
    plural: 'usuários',
    noneFoundMessage: 'Nenhum usuário encontrado',
  },
  membro: {
    plural: 'membros',
    noneFoundMessage: 'Nenhum membro encontrado',
  },
  notificação: {
    plural: 'notificações',
    gender: 'female',
    noneFoundMessage: 'Nenhuma notificação encontrada',
  },
  'atividade funcional': {
    plural: 'atividades funcionais',
    gender: 'female',
    noneFoundMessage: 'Nenhuma atividade funcional encontrada',
  },
  local: {
    plural: 'locais',
    noneFoundMessage: 'Nenhum local encontrado',
  },
  treinamento: {
    plural: 'treinamentos',
    noneFoundMessage: 'Nenhum treinamento encontrado',
  },
  conteúdo: {
    plural: 'conteúdos',
    noneFoundMessage: 'Nenhum conteúdo encontrado',
  },
  colaborador: {
    plural: 'colaboradores',
    noneFoundMessage: 'Nenhum colaborador encontrado',
  },
  log: {
    plural: 'logs',
    noneFoundMessage: 'Ops! Parece que ainda não há conteúdo nessa página',
  },
  matriz: {
    plural: 'matrizes',
    gender: 'female',
    noneFoundMessage: 'Nenhuma matriz encontrada',
  },
} as const;

export const DEFAULT_ENTITY: EntityType = 'documento';

export function isValidEntityType(value: unknown): value is EntityType {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ENTITY_CONFIGS, value);
}

export interface UsePaginationEntitiesReturn {
  getEntityConfig: (entityName?: string) => EntityConfig;
  getEntityPlural: ComputedRef<string>;
  getNoneFoundMessage: ComputedRef<string>;
  isValidEntity: (value: unknown) => value is EntityType;
}

export function usePaginationEntities(entityName?: string): UsePaginationEntitiesReturn {
  const getEntityConfig = (entityName?: string): EntityConfig => {
    if (!entityName) {
      return ENTITY_CONFIGS[DEFAULT_ENTITY];
    }

    if (!isValidEntityType(entityName)) {
      return ENTITY_CONFIGS[DEFAULT_ENTITY];
    }

    return ENTITY_CONFIGS[entityName];
  };

  const config = computed(() => getEntityConfig(entityName));

  const getEntityPlural = computed(() => config.value.plural);
  const getNoneFoundMessage = computed(() => config.value.noneFoundMessage);

  return {
    getEntityConfig,
    getEntityPlural,
    getNoneFoundMessage,
    isValidEntity: isValidEntityType,
  };
}
